import EquationInput from "../../../../components/inputs/EquationInput";
import { InlineMath, BlockMath } from "react-katex";
import { checkLineEquation } from "../../../../utils/lineEquation";
import FractionInput, {
  formatLatexPreview,
  parseFractionLikeInput,
} from "../../../../components/inputs/FractionInput";


function toIntegerGeneralFormFromFraction(aNum, aDen, bNum, bDen) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const lcm = (a, b) => (!a || !b) ? 0 : Math.abs(a * b) / gcd(a, b);

  const wsp = lcm(aDen, bDen);

  let A = -aNum * (wsp / aDen);
  let B = wsp;
  let C = -bNum * (wsp / bDen);

  const wspGcd = gcd(gcd(Math.abs(A), Math.abs(B)), Math.abs(C));
  A = A / wspGcd;
  B = B / wspGcd;
  C = C / wspGcd;

  return { A, B, C };
}

function formatFraction(numerator, denominator) {
  const gcd = (a, b) => b === 0 ? Math.abs(a) : gcd(b, a % b);

  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);

  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}


export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="space-y-2">
      <div>
        <EquationInput
          value={userAnswer || ""}
          onChange={(val) => onChange(val)}
          disabled={disabled}
          placeholder="np. 2x - y + 4 = 0"
        />
      </div>
    </div>
  );
}


export function checkAnswer({ data }, userAnswer) {
  return checkLineEquation(userAnswer, data, { form: "general" });
}


export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string" || userAnswer.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
      <InlineMath math={userAnswer} />
  );
}

export function renderAnswer({ data }) {
  const { A, B, C } = data;

  const formatTerm = (coef, variable) => {
    if (coef === 0) return null;
    if (coef === 1) return `${variable}`;
    if (coef === -1) return `-${variable}`;
    return `${coef}${variable}`;
  };

  const parts = [
    formatTerm(A, "x"),
    formatTerm(B, "y"),
    C !== 0 ? `${C}` : null
  ].filter(Boolean);

  const generalForm = parts.join(" + ").replace(/\+\s\-/g, "- ");

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <div style={{ display: "block", marginTop: "0.5rem" }}>
        <InlineMath math={`\\displaystyle ${generalForm} = 0`} />
      </div>
    </div>
  );
}


export function renderExplanation({ data }) {
  const { x1, y1, x2, y2, A, B, C } = data;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const fs = (v) => (v < 0 ? `(${v})` : v);
  const parenIfNeg = (v) => (v.startsWith("-") ? `(${v})` : v);

  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));

  const slopeGcd = gcd(dy, dx);
  const simpNum = dy / slopeGcd;
  const simpDen = dx / slopeGcd;
  const aLatex = formatFraction(simpNum, simpDen);

  const bNum = y1 * simpDen - simpNum * x1;
  const bDen = simpDen;
  const bLatex = formatFraction(bNum, bDen);

  const axNum = simpNum * x1;
  const axDen = simpDen;
  const axGcd = gcd(axNum, axDen);
  const axSimpNum = axNum / axGcd;
  const axSimpDen = axDen / axGcd;
  const axLatex = formatFraction(axSimpNum, axSimpDen);

  const b = bNum / bDen;

  const formatTerm = (coef, variable) => {
    if (coef === 0) return null;
    if (coef === 1) return `${variable}`;
    if (coef === -1) return `-${variable}`;
    return `${coef}${variable}`;
  };

  const parts = [
    formatTerm(A, "x"),
    formatTerm(B, "y"),
    C !== 0 ? `${C}` : null
  ].filter(Boolean);

  const generalForm = parts.join(" + ").replace(/\+\s\-/g, "- ");

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Obliczamy współczynnik kierunkowy prostej przechodzącej przez punkty A i B:</p>
      <BlockMath math="a = \dfrac{y_B - y_A}{x_B - x_A}" />
      <BlockMath math={`a = \\dfrac{${fs(y2)} - ${fs(y1)}}{${fs(x2)} - ${fs(x1)}} = \\dfrac{${dy}}{${dx}} = ${aLatex}`} />

      <p><strong>Krok 2:</strong> Obliczamy wyraz wolny <InlineMath math="b" /> ze wzoru:</p>
      <BlockMath math="b = y - ax" />
      <p>Podstawiamy współrzędne punktu <InlineMath math={`A = (${x1}, ${y1})`} /> oraz <InlineMath math={`a = ${aLatex}`} />:</p>
      <BlockMath
        math={`b = ${fs(y1)} - (${aLatex} \\cdot ${fs(x1)}) = ${fs(y1)} - ${parenIfNeg(axLatex)} = ${bLatex}`}
      />

      <p><strong>Krok 3:</strong> Równanie prostej w postaci kierunkowej:</p>
      <BlockMath
        math={
          simpNum === 0
            ? `y = ${bLatex}`
            : `y = ${aLatex}x ${b >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`
        }
      />

      <p><strong>Krok 4:</strong> Przekształcamy do postaci ogólnej z całkowitymi współczynnikami:</p>
      <BlockMath math={`\\displaystyle ${generalForm} = 0`} />
    </div>
  );
}




export function generateTask() {
  const rand = () => Math.floor(Math.random() * 17) - 8;
  let x1, y1, x2, y2;

  do {
    x1 = rand();
    y1 = rand();
    x2 = rand();
    y2 = rand();
  } while (x1 === x2); // unikamy dzielenia przez 0

  const dx = x2 - x1;
  const dy = y2 - y1;

  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const slopeGcd = gcd(dy, dx);
  const simpNum = dy / slopeGcd;
  const simpDen = dx / slopeGcd;
  const a = simpNum / simpDen;

  const bNum = y1 * simpDen - simpNum * x1;
  const bDen = simpDen;
  const b = bNum / bDen;

  const { A, B, C } = toIntegerGeneralFormFromFraction(simpNum, simpDen, bNum, bDen);

  return {
    type: "FindGeneralLineTwoPointsTask",
    data: {
      x1,
      y1,
      x2,
      y2,
      A,
      B,
      C
    }
  };
}


export function renderTask({ data }) {
  const { x1, y1, x2, y2 } = data;

  return (
    <>
      <p>Wyznacz równanie ogólne prostej przechodzącej przez punkty:{" "}
        <InlineMath math={`A = (${x1}, ${y1})`} />,{" "}
        <InlineMath math={`B = (${x2}, ${y2})`} />.
      </p>
    </>
  );
}
